import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { getProjectWithToken } from '../data/pages/auth'
import AppLayout from '../layouts/AppLayout.vue'
import AuthLayout from '../layouts/AuthLayout.vue'
import RouteViewComponent from '../layouts/RouterBypass.vue'
import RouterPassthrough from '../layouts/RouterPassthrough.vue'
import { useAuthStore } from '../stores/auth'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
  {
    name: 'user',
    path: '/user',
    component: AppLayout,
    props: { isAdmin: false },
    meta: {
      guard: 'user',
    },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/user/dashboard/Dashboard.vue'),
      },
      {
        name: 'kanban',
        path: 'kanban',
        component: () => import('../pages/user/kanban/Kanban.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/user/projects/Projects.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/user/preferences/Preferences.vue'),
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/user/profile/Profile.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    props: { isAdmin: true },
    meta: {
      guard: 'admin',
    },
    redirect: { name: 'admin_dashboard' },
    children: [
      {
        name: 'admin_dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'admin_users',
        path: 'users',
        component: () => import('../pages/admin/users/UsersPage.vue'),
      },
      {
        name: 'admin_projects',
        path: 'projects',
        component: () => import('../pages/admin/projects/Projects.vue'),
      },
      {
        name: 'admin_finance',
        path: 'finance',
        component: RouterPassthrough,
        children: [
          {
            name: 'admin_agreements',
            path: 'agreements',
            component: () => import('../pages/admin/billing/Agreements.vue'),
          },
          {
            name: 'admin_billing',
            path: 'billing',
            component: () => import('../pages/admin/billing/Bills.vue'),
          },
        ],
      },
      {
        name: 'admin_config',
        path: 'config',
        component: RouterPassthrough,
        children: [
          {
            name: 'admin_receivers',
            path: 'receivers',
            component: () => import('../pages/admin/config/Receivers.vue'),
          },
          {
            name: 'admin_other',
            path: 'other',
            component: () => import('../pages/admin/config/Other.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    meta: {
      guard: 'guest',
    },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'index',
    path: '/',
    component: () => import('../pages/Index.vue'),
  },
  {
    name: 'order',
    path: '/order',
    component: () => import('../pages/Order.vue'),
  },
  {
    name: 'sequence',
    path: '/sequence',
    component: () => import('../pages/Sequence.vue'),
  },
  {
    name: 'manual',
    path: '/manual/:id',
    component: () => import('../pages/Manual.vue'),
    props: (route) => ({ project: route.meta.project }),
    beforeEnter: async (to) => {
      if (to.query.token && to.params.id) {
        try {
          const project = await getProjectWithToken(Number(to.params.id), String(to.query.token))
          to.meta.project = project
          return true
        } catch {
          console.log(Number(to.params.id), String(to.query.token))
        }
      }
      return { name: 'index' }
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from) => {
  console.log(`${new Date(Date.now()).toLocaleString('en-CA')}: route to ${to.fullPath}, from ${from.fullPath}`)

  const authStore = useAuthStore()
  await authStore.getProfile()
  if (
    (to.meta && to.meta.guard == 'guest' && authStore.isLoggedIn) ||
    (to.meta && to.meta.guard == 'user' && !authStore.isLoggedIn) ||
    (to.meta && to.meta.guard == 'admin' && (!authStore.isLoggedIn || !authStore.isAdmin))
  ) {
    return { name: 'index' }
  }
})

export default router
